import { Button, Table, Typography, Select } from "antd";
import { useNavigate } from "react-router-dom";
import { useCompany } from "../../hooks/useCompany";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { CloudDownloadOutlined, CloudUploadOutlined } from "@mui/icons-material";
import { useEffect, useState } from "react";
import useLoading from "../../hooks/useLoading";
import { api } from "../../services/axios";
import { toast } from "react-toastify";
import type { TableProps } from 'antd';
import ModalDelete from "../../components/Modal/ModalDelete/index.tsx";
import { ModalDeleteMassiveOtherExpenditure } from "../../components/OtherExpenditure/ModalDeleteMassiveOtherExpenditure";
import useModal from "../../hooks/useModal";
import { UploadModal } from "../../components/Shared/UploadModal";
import { formatCnpj } from "../../utils/masks";

interface OtherExpenditureModel {
    id: string;
    typology: string;
    supplier: string;
    cnpj: string;
    projectName: string;
    trackingNumber: string;
    accountingDate: Date;
    expenditureValue: number;
}

export default function OtherExpenditure() {
    const navigate = useNavigate();
    const { company } = useCompany();
    const { toggleLoading, loading } = useLoading();
    const { Text } = Typography;

    const [otherExpenditures, setOtherExpenditures] = useState<OtherExpenditureModel[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [take, setTake] = useState(7);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemToDelete, setItemToDelete] = useState<string>("");
    const [word, setWord] = useState<string>("");
    const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
    const [cnpjs, setCnpjs] = useState<{ value: string, label: string }[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [isMassiveDeleteModalOpen, setIsMassiveDeleteModalOpen] = useState(false);
    const { isShowing: isShowingExcelModal, toggle: toggleExcelModal } = useModal();
    const [fileToUpload, setFileToUpload] = useState<Blob>();

    function goToCreatePage() {
        navigate(`/${company.serviceId}/CreateOtherExpenditure/materials`);
    }

    function goToEditPage(id: string) {
        navigate(`/${company.serviceId}/CreateOtherExpenditure/materials/${id}`);
    }

    const formatDate = (dateString: Date) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
    };

    const formatCurrency = (value: number) => {
        return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const handleDownloadFile = (url: string, filename: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleApiError = (error: any) => {
        const message = error.response?.data?.message || error.message || 'Erro desconhecido';
        toast.error(message);
    };

    async function getOtherExpenditures(page?: number, pageSize?: number, searchWord?: string) {
        toggleLoading(true);
        try {
            let url = `OtherExpenditure/GetAllPaginated/${company.serviceId}/${page ?? 1}/${pageSize ?? take}`;
            const currentWord = searchWord ?? word;
            if (currentWord) {
                const cleanWord = currentWord.replace(/[.\/-]/g, '');
                url += `?word=${cleanWord}`;
            }
            const resp = await api.get(url);
            const response = resp.data;
            if (response.statusCode === 200) {
                setOtherExpenditures(response.data.items);
                setTotal(response.data.total);
          
            } else {
                toast.error("Erro ao buscar dispêndios.");
            }
        } catch (error: any) {
            handleApiError(error);
        } finally {
            toggleLoading(false);
        }
    }

    async function deleteOtherExpenditure(id: string) {
        toggleLoading(true);
        try {
            const payload = { id: id };
            const resp = await api.delete("OtherExpenditure/DeleteItem", { data: payload });
            const response = resp.data;
            if (response.statusCode === 200) {
                toast.success("Dispêndio deletado com sucesso!");
                setIsModalDeleteOpen(false);
                getOtherExpenditures(currentPage, take);
            } else {
                toast.error(`Erro ao deletar: ${response.message}`);
            }
        } catch (error: any) {
            handleApiError(error);
        } finally {
            toggleLoading(false);
        }
    }

    async function deleteMassiveOtherExpenditures() {
        toggleLoading(true);
        try {
            const payload = { ids: selectedRowKeys };
            const response = await api.delete("OtherExpenditure/DeleteMassive", { data: payload });

            if (response.data.statusCode === 200) {
                toast.success("Itens deletados com sucesso!");
                setSelectedRowKeys([]);
                getOtherExpenditures(currentPage, take);
            } else {
                toast.error(`Erro ao deletar: ${response.data.message}`);
            }
        } catch (error: any) {
            handleApiError(error);
        } finally {
            toggleLoading(false);
            setIsMassiveDeleteModalOpen(false);
        }
    }

    async function handleDownload() {
        toggleLoading(true);
        try {
            const response = await api.get(
                `OtherExpenditure/${company.serviceId}/${company.companyId}`,
                { responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            handleDownloadFile(url, 'OutrosDispendios.xlsx');
            toast.success("Download realizado com sucesso!");
        } catch (error: any) {
            handleApiError(error);
        } finally {
            toggleLoading(false);
        }
    }

    async function handleUploadExcel() {
        try {
            toggleLoading(true);
            toggleExcelModal();

            const formData = new FormData();
            if (fileToUpload) {
                formData.append('File', fileToUpload);
            }
            formData.append('ServiceId', company.serviceId);
            formData.append('TaxNumber', company.taxNumber);
            formData.append('Year', String(company.year));

            const response = await api.post('OtherExpenditure/UploadExcel', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response.data.statusCode === 200) {
                toast.success('Arquivo enviado com sucesso! A importação está sendo processada.');
                getOtherExpenditures();
            } else {
                toast.error(`Erro no upload: ${response.data.message || response.data.errors.join(', ')}`);
            }
        } catch (error: any) {
            handleApiError(error);
        } finally {
            toggleLoading(false);
        }
    }

    async function getCnpjsForFilter() {
        try {
            const resp = await api.get(`Material/GetAllSelects/${company.serviceId}/${company.companyId}`);
            const response = resp.data;
            if (response.statusCode === 200) {
                const uniqueCnpjs = response.data.associatedCNPJ.filter(
                    (item: any, index: number, self: any[]) =>
                        index === self.findIndex((t) => t.value === item.value)
                );

                const cnpjOptions = uniqueCnpjs.map((item: any) => ({
                    key: item.value,
                    value: item.label,
                    label: item.label
                }));
                setCnpjs(cnpjOptions);
            }
        } catch (error: any) {
            handleApiError(error);
        }
    }

    const handleShowSizeChange = (current: number, size: number) => {
        setTake(size);
        setCurrentPage(current);
        getOtherExpenditures(current, size);
    };

    const otherExpendituresColumns: TableProps<OtherExpenditureModel>['columns'] = [
        { title: 'Nome do Projeto', dataIndex: 'projectName', key: 'projectName', sorter: (a, b) => a.projectName.localeCompare(b.projectName) },
        { title: 'Tipologia', dataIndex: 'typology', key: 'typology', sorter: (a, b) => a.typology.localeCompare(b.typology) },
        { title: 'Fornecedor', dataIndex: 'supplier', key: 'supplier', sorter: (a, b) => a.supplier.localeCompare(b.supplier) },
        { title: 'CNPJ', dataIndex: 'supplierCNPJ', key: 'supplierCNPJ', render: (value) => formatCnpj(value) },
        { title: 'N° Rastreio', dataIndex: 'trackingNumber', key: 'trackingNumber' },
        {
            title: 'Data de Contabilização',
            dataIndex: 'accountingDate',
            key: 'accountingDate',
            render: (value) => formatDate(value),
            sorter: (a, b) => new Date(a.accountingDate).getTime() - new Date(b.accountingDate).getTime(),
        },
        {
            title: 'Valor Dispêndio',
            dataIndex: 'expenditureValue',
            key: 'expenditureValue',
            render: (value) => formatCurrency(value),
            sorter: (a, b) => a.expenditureValue - b.expenditureValue,
        },
        {
            title: 'Ações',
            dataIndex: 'id',
            key: 'id',
            render: (value) => (
                <div>
                    <Button type="text" size="small" style={{ height: "24px", width: "24px" }} onClick={() => goToEditPage(value)}><EditOutlined className="blue" /></Button>
                    <Button type="text" size="small" style={{ height: "24px", width: "24px" }} onClick={() => { setItemToDelete(value); setIsModalDeleteOpen(true); }}><DeleteOutlined className="red-delete" /></Button>
                </div>
            )
        }
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (newSelectedRowKeys: React.Key[]) => setSelectedRowKeys(newSelectedRowKeys),
    };

    useEffect(() => {
        getOtherExpenditures();
        getCnpjsForFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div>
            <div className='d-flex justify-content-between align-items-center mt-4 mb-4'>
                <div className='flex justify-content-start'>
                    <Select
                        className='me-2 material-buttons'
                        placeholder='Selecione um CNPJ cadastrado...'
                        onChange={(value) => setWord(value)}
                        dropdownStyle={{ maxHeight: 300, overflowY: 'auto' }}
                        style={{ width: "274px", height: "40px" }}
                        allowClear
                    >
                        {cnpjs.map((option, index) => (
                            <Select.Option key={index} value={option.value}>{option.label}</Select.Option>
                        ))}
                    </Select>
                    <Button className='material-button-blue' type='primary' style={{ height: "40px" }} onClick={() => getOtherExpenditures(1, take, word)}>
                        <div className='text-white'>
                            Filtrar
                        </div>
                    </Button>
                </div>

                <div className='d-flex justify-content-end align-items-center'>
                    <Button icon={<CloudDownloadOutlined />} size="large" className='me-2 material-buttons' onClick={handleDownload}>Download</Button>
                    <Button icon={<CloudUploadOutlined />} size="large" className='me-2 material-buttons' onClick={toggleExcelModal}>Upload</Button>
                    <Button
                        type='primary'
                        size="large"
                        className='me-2 material-buttons material-button-blue'
                        onClick={goToCreatePage}
                    >
                        <div className='text-white'>
                            <PlusOutlined className='me-2' />
                            Inserir Dispêndio
                        </div>
                    </Button>
                    <Button
                        size="large"
                        danger
                        icon={<DeleteOutlined className='text-red' />}
                        className='me-2 material-buttons'
                        disabled={selectedRowKeys.length === 0}
                        onClick={() => setIsMassiveDeleteModalOpen(true)}
                    >
                        <span className='text-red'>Delete Massivo</span>
                    </Button>
                </div>
            </div>
            <Table
                rowSelection={rowSelection}
                loading={loading}
                rowKey="id"
                columns={otherExpendituresColumns}
                dataSource={otherExpenditures}
                pagination={{
                    total: total,
                    showSizeChanger: true,
                    showTotal: (total) => <Text>Total {total} items</Text>,
                    pageSize: take,
                    current: currentPage,
                    onShowSizeChange: (page, size) => handleShowSizeChange(page, size),
                    onChange: (page, size) => handleShowSizeChange(page, size),
                }}
            />
            <ModalDelete setOpen={setIsModalDeleteOpen} id={itemToDelete} operation={deleteOtherExpenditure} itemName='Dispêndio' open={isModalDeleteOpen} />
            <ModalDeleteMassiveOtherExpenditure
                isOpen={isMassiveDeleteModalOpen}
                setOpen={setIsMassiveDeleteModalOpen}
                operation={deleteMassiveOtherExpenditures}
                itemsCount={selectedRowKeys.length}
            />
            {isShowingExcelModal &&
                <UploadModal
                    action={handleUploadExcel}
                    isShowing={isShowingExcelModal}
                    toggle={toggleExcelModal}
                    setFile={setFileToUpload}
                />
            }
        </div>
    );
}
